import LocalizedStrings from 'react-localization';
import moment from 'moment';
import 'moment/locale/fr'; // moved to App.js for "global"esque configuration
import enStrings from './strings/en.json';
import frcaStrings from './strings/en_FR_TELUS218113.json';
import lsUtil from 'APP_ROOT/components/common/lsUtil';
import Events from 'APP_ROOT/components/common/events';
import {eventStrings} from 'APP_ROOT/data/events';

export const strings = new LocalizedStrings({
    en: { ...enStrings, ...eventStrings('en') },
    fr: { ...frcaStrings, ...eventStrings('fr') }
});

function getLocaleFromPath() {

    let pathname = window.location.pathname; // e.g. "/en/dashboard"
    if (pathname.charAt(0) === "/") {
        pathname = pathname.substring(1); // e.g. "en/dashboard"
    }
    
    let languageTag = undefined; //default, no region ("The golden rule when creating language tags is to keep the tag as short as possible", https://www.w3.org/International/articles/language-tags/)
    
    const pathnameComponents = pathname.split("/");
    if (pathnameComponents.length > 0) {
        languageTag = pathnameComponents[0];
    }
    return languageTag;
}

export function getLanguageSubtag (languageTag) { // "The golden rule when creating language tags is to keep the tag as short as possible", https://www.w3.org/International/articles/language-tags/

    if (!languageTag) {
        console.warn("localization getLanguageSubtag languageTag is undefined/null");
        return languageTag;
    }

    let goldenRuleLanguageTag = languageTag;
    
    if (goldenRuleLanguageTag.includes("_")) { // sanitize variants noted in other OSs, https://stackoverflow.com/questions/3191664/list-of-all-locales-and-their-short-codes
        goldenRuleLanguageTag = goldenRuleLanguageTag.replace("_","-");
    }

    // "Most language tags consist of a two- or three-letter language subtag." https://www.w3.org/International/articles/language-tags/
    const languageTagComponents = goldenRuleLanguageTag.split("-");
    goldenRuleLanguageTag = languageTagComponents[0]; // get the language subtag
    if (goldenRuleLanguageTag.length < 2 ||
        goldenRuleLanguageTag.length > 3) {
        console.warn("localization getLanguageSubtag detected invalid or missing lang subtag for '" + languageTag + "'");
        return;
    }
    return goldenRuleLanguageTag.toLowerCase(); // ASSUMING this 2- or 3-character tag is PROBABLY valid
}

function switchForLanguageSubtag (languageSubtag) {

    const pathname = window.location.pathname; // e.g. "/en/dashboard"
    const pathnameComponents = pathname.split("/");

    switch (getLanguageSubtag(pathnameComponents[1])) {
        case "en":
        case "fr":
            pathnameComponents[1] = languageSubtag; // replace
            break;
        default:
            console.error("localization switchForLanguageSubtag could not find known language for redirect.");
            console.warn("To avoid the above error, see App.js validRedirectParams usage in router");
            return;
    }
    const redirectPath = pathnameComponents.join("/") + window.location.search;
    document.dispatchEvent(Events.showLoading({ message: strings.app_loading }));
    window.location.href = redirectPath;
}


// determines the current locale and sanitizes the corresponding url param via a redirect if necessary
// - using it to set langugage in links throughout the web app
// - using it to set locales for Date's toLocaleString, toLocaleDateString, toLocaleTimeString
// - using it to set locale for moment, see also locale import in App.js
// - if a user has set their preferred language, getCurrentLocale will redirect if necessary, as well
export function getCurrentLocale() {

    const localeFromPath = getLocaleFromPath().toLowerCase();
    const languageSubtagFromPath = getLanguageSubtag(localeFromPath);

    let sanitzedLanguageSubtagFromPath = undefined;
    switch (languageSubtagFromPath) { // switch to other supported language, if subtag match detected
        case 'fr':
            sanitzedLanguageSubtagFromPath = 'fr';
            break;
        default:
            sanitzedLanguageSubtagFromPath = 'en';
            break;
    }

    let currentLocale = sanitzedLanguageSubtagFromPath; // can be undefined

    // allow user setting to override the derived locale from path
    const userLocale = getUserLocale();

    if (userLocale) {
        currentLocale = userLocale;
    }

    if (localeFromPath !== currentLocale) {
        switchForLanguageSubtag(currentLocale);
        return currentLocale;
    }

    moment.locale(currentLocale); // in order for this to work throughout app (wherever strings are used), include moment and locale in App.js
    return currentLocale;
}

export function getUserLocale() {

    const userLanguageSubtag = lsUtil.getItem('localstorage.user.general.locale') || null;
    return userLanguageSubtag;
}

export function setUserLocale(locale) {

    let languageSubtag = getLanguageSubtag(locale);
    switch (languageSubtag) {
        case 'fr':
        case 'en':
            lsUtil.setItem('localstorage.user.general.locale', languageSubtag);
            getCurrentLocale(); // trigger the redirect        
            return;
        default:
            console.error("localization setUserLocale invalid locale, " + locale);
            return;
    }
}

export function getMissingTranslationString() {
    const englishStrings = { ...enStrings };
    const frenchStrings = { ...frcaStrings };

    const missingStrings = [];

    for (const key in englishStrings) {
        if (!frenchStrings[key] || frenchStrings[key] === '') {
            missingStrings.push({
                key,
                from: 'en',
                to: 'fr'
            });
        }
        else{
            delete frenchStrings[key];
        }
    }

    for (const key in frenchStrings) {
        if (!englishStrings[key]) {
            missingStrings.push({
                key,
                from: 'fr',
                to: 'en'
            });
        }
    }

    return missingStrings;
}

export function getSameTranslationString() {
    const englishStrings = { ...enStrings };
    const frenchStrings = { ...frcaStrings };

    const missingStrings = [];

    for (const key in englishStrings) {
        if (frenchStrings[key]  && frenchStrings[key] === englishStrings[key]) {
            missingStrings.push({
                key,
                from: englishStrings[key],
                to: frenchStrings[key]
            });
        }
    }

    return missingStrings;
}


const locale = getCurrentLocale();
strings.setLanguage(locale);
